/**
 * PayByCheckSheet (Plan 14, scope item 5 — school self-report path).
 *
 * The preferred reconciliation path: school admin clicks "Pay by
 * check" on an invoice drawer → this sheet captures check number,
 * date mailed, amount (default = invoice outstanding), optional check
 * image, optional memo. On submit, we POST to
 * `/api/billing/school/invoices/:id/pay-by-check`.
 *
 * Photo upload uses the existing `/api/s3/presign` route (also a stub
 * until S3 lands); the sheet keeps the photo as an opaque object key
 * since the SchoolAR row doesn't decode the asset directly.
 */

import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@ordo/ui/input-group'
import { Label } from '@ordo/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { toast } from '@ordo/ui/sonner'
import { Textarea } from '@ordo/ui/textarea'
import { useEffect, useState } from 'react'

import { type SchoolARInvoiceRow, usePayByCheckMutation } from '@/api/billing-school'

import { formatCents, outstandingCents } from './school-ar-helpers'

export interface PayByCheckSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoice: SchoolARInvoiceRow
}

export function PayByCheckSheet({ open, onOpenChange, invoice }: PayByCheckSheetProps) {
  const outstanding = outstandingCents(invoice)
  const [checkNumber, setCheckNumber] = useState('')
  const [mailedAt, setMailedAt] = useState<string>(() => new Date().toISOString().slice(0, 10))
  const [amountInput, setAmountInput] = useState<string>(() => (outstanding / 100).toFixed(2))
  const [memo, setMemo] = useState('')

  useEffect(() => {
    if (open) {
      setCheckNumber('')
      setMailedAt(new Date().toISOString().slice(0, 10))
      setAmountInput((outstanding / 100).toFixed(2))
      setMemo('')
    }
  }, [open, outstanding])

  const mutation = usePayByCheckMutation()

  const handleSubmit = async () => {
    const amountCents = Math.round(Number(amountInput) * 100)
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      toast.error('Enter the amount mailed.')
      return
    }
    if (!checkNumber.trim()) {
      toast.error('Enter the check number.')
      return
    }
    if (!mailedAt) {
      toast.error('Enter the date the check was mailed.')
      return
    }
    try {
      await mutation.mutateAsync({
        invoiceId: invoice.id,
        checkNumber: checkNumber.trim(),
        mailedAt: new Date(mailedAt).toISOString(),
        amountCents,
        memo: memo.trim() || null,
      })
      toast.success('Check submitted — finance will confirm on receipt.')
      onOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error('Could not submit check', { description: message })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg" className="flex flex-col gap-0 p-0">
        <SheetHeader className="shrink-0 border-b px-6 pb-4 pt-6">
          <SheetTitle>Pay by check</SheetTitle>
          <SheetDescription>
            Submitting marks the invoice as awaiting check confirmation. Finance reconciles each check against Brex
            deposits and confirms receipt on this end.
          </SheetDescription>
        </SheetHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit()
          }}>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="check-number">Check number</Label>
              <Input
                id="check-number"
                type="text"
                placeholder="e.g. 1042"
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="check-mailed-at">Date mailed</Label>
              <Input id="check-mailed-at" type="date" value={mailedAt} onChange={(e) => setMailedAt(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="check-amount">Amount</Label>
              <InputGroup>
                <InputGroupAddon>$</InputGroupAddon>
                <InputGroupInput
                  id="check-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountInput}
                  readOnly
                  onChange={(e) => setAmountInput(e.target.value)}
                />
              </InputGroup>
              <p className="text-xs text-muted-foreground">
                V1 accepts the full outstanding balance only: {formatCents(outstanding, invoice.currency)}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="check-memo">Memo (optional)</Label>
              <Textarea
                id="check-memo"
                rows={3}
                placeholder="Any context for finance (PO #, payer name, etc.)"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </div>

            <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-xs text-muted-foreground">
              A check image upload affordance lives here when the S3 presigning ships (follow-up plan).
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : 'Submit check'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
